import { type ReactNode, useEffect, useId, useState } from 'react'
import { Alert } from 'app/components/workflows/inspectors/Alert'
import { Disclosure } from 'app/components/workflows/inspectors/Disclosure'
import { Button } from 'app/components/ui/button'
import { Checkbox } from 'app/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import type { SmsConsentText } from './smsConsentText'

type SmsConsentModalProps = {
  open: boolean
  workspaceName: string
  /** The versioned copy from smsConsentText.ts. This component renders it; it never supplies wording of its own. */
  text: SmsConsentText
  submitting: boolean
  error?: string
  onAgree: () => void
  /** Cancel, Esc, the close button, and a click outside all land here. */
  onCancel: () => void
  /** Radix returns focus to whatever had it; preventDefault here to send it somewhere else. */
  onCloseAutoFocus?: (event: Event) => void
}

/** The served copy marks bold with `**` and leaves `{workspaceName}` for the client to fill. */
function renderCopy(copy: string, workspaceName: string): ReactNode[] {
  return copy
    .split(`{workspaceName}`)
    .join(workspaceName)
    .split(`**`)
    .map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part))
}

export function SmsConsentModal({
  open,
  workspaceName,
  text,
  submitting,
  error,
  onAgree,
  onCancel,
  onCloseAutoFocus,
}: SmsConsentModalProps) {
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const hintId = useId()
  const idPrefix = useId()
  // Every open starts from nothing checked: an affirmation is made once, deliberately, each time.
  useEffect(() => {
    if (open) setChecked(new Set())
  }, [open])

  const allChecked = text.statements.every((statement) => checked.has(statement.key))

  const toggle = (key: string, next: boolean) =>
    setChecked((current) => {
      const updated = new Set(current)
      if (next) updated.add(key)
      else updated.delete(key)
      return updated
    })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !submitting && onCancel()}>
      <DialogContent
        className="flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-xl"
        aria-describedby={undefined}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <div className="border-b border-default px-5 py-4 pr-12">
          <DialogHeader className="text-left">
            <DialogTitle>{text.heading}</DialogTitle>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm text-default">
          <p>{renderCopy(text.intro, workspaceName)}</p>
          <p>{renderCopy(text.confirmPrompt, workspaceName)}</p>

          <ul className="space-y-1">
            {text.statements.map((statement) => {
              const id = `${idPrefix}-${statement.key}`
              return (
                <li key={statement.key}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-start gap-3 rounded-lg p-2 hover:bg-surface-muted"
                  >
                    <Checkbox
                      id={id}
                      className="mt-0.5"
                      checked={checked.has(statement.key)}
                      disabled={submitting}
                      onCheckedChange={(next) => toggle(statement.key, next === true)}
                    />
                    <span className="min-w-0">
                      <strong className="text-strong">{statement.title}</strong>
                      {` `}
                      {renderCopy(statement.body, workspaceName)}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>

          <Disclosure title={text.examplesTitle}>
            <ul className="list-disc space-y-1.5 pl-5">
              {text.examples.map((example) => (
                <li key={example}>{renderCopy(example, workspaceName)}</li>
              ))}
            </ul>
          </Disclosure>

          <p className="text-xs text-subtle italic">{renderCopy(text.disclaimer, workspaceName)}</p>

          {error && (
            <Alert tone="warn" lead="Couldn't record your confirmation">
              {error}
            </Alert>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-default px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Stacked (below `sm`, with DialogFooter) a spent hint would leave a blank band above the
              buttons; side by side it keeps its width so the buttons do not jump. */}
          <p
            id={hintId}
            className={`text-xs text-subtle ${allChecked ? `hidden sm:invisible sm:block` : ``}`}
            aria-hidden={allChecked}
          >
            Check every box to continue
          </p>
          <DialogFooter>
            <Button variant="tertiary" disabled={submitting} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              disabled={!allChecked || submitting}
              loading={submitting}
              aria-describedby={allChecked ? undefined : hintId}
              onClick={onAgree}
            >
              {text.agreeLabel}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
